import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Receipt, Building, CreditCard, DollarSign, CheckCircle, XCircle, AlertTriangle, Eye, Loader2, CheckCheck } from 'lucide-react';
import { toast } from 'sonner';
import AppShell from '../components/AppShell';

export default function FinanceDashboard() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [feeSchedules, setFeeSchedules] = useState([]);
  const [paySchedules, setPaySchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const [invRes, orgRes, feeRes, payRes] = await Promise.all([
        adminAPI.getInvoices(), adminAPI.getOrgs(), adminAPI.getFeeSchedules(), adminAPI.getPaySchedules()
      ]);
      setInvoices(invRes.data);
      setOrgs(orgRes.data);
      setFeeSchedules(feeRes.data);
      setPaySchedules(payRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const viewInvoice = async (inv) => {
    setDetailLoading(true);
    try {
      const res = await adminAPI.getInvoice(inv.id);
      setSelectedInvoice(res.data);
    } catch {
      setSelectedInvoice(inv);
    }
    setDetailLoading(false);
  };

  const handleInvoiceAction = async (invoiceId, action) => {
    try {
      if (action === 'approve') await adminAPI.approveInvoice(invoiceId);
      else if (action === 'reject') await adminAPI.rejectInvoice(invoiceId);
      else if (action === 'pay') await adminAPI.payInvoice(invoiceId);
      toast.success(`Invoice ${action}d`);
      setSelectedInvoice(null);
      load();
    } catch (e) { toast.error(`Failed to ${action} invoice`); }
  };

  const handleApproveItem = async (invoiceId, idx) => {
    try {
      const api = (await import('../lib/api')).invoiceAPI;
      await api.approveItem(invoiceId, idx);
      toast.success('Line item approved');
      viewInvoice({ id: invoiceId });
    } catch { toast.error('Failed'); }
  };

  const handleBatchApprove = async (invoiceId) => {
    const items = selectedInvoice?.line_items || [];
    const indices = items.map((_, i) => i).filter(i => items[i].item_status !== 'approved');
    try {
      const api = (await import('../lib/api')).invoiceAPI;
      await api.batchApprove(invoiceId, indices);
      toast.success(`${indices.length} items approved`);
      viewInvoice({ id: invoiceId });
    } catch { toast.error('Failed'); }
  };

  const statusBadge = (status) => {
    const map = { submitted: 'bg-blue-500/10 text-blue-600', approved: 'bg-green-500/10 text-green-600', rejected: 'bg-red-500/10 text-red-600', paid: 'bg-emerald-500/10 text-emerald-600' };
    return <Badge className={`${map[status] || ''} text-[10px]`}>{status}</Badge>;
  };

  if (loading) return <AppShell><div className="flex items-center justify-center h-[calc(100vh-3.5rem)]"><Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--brand))]" /></div></AppShell>;

  const pendingInvoices = invoices.filter(i => i.status === 'submitted');
  const flaggedInvoices = invoices.filter(i => i.has_discrepancy);

  return (
    <AppShell>
      <div className="p-6 md:p-8 max-w-6xl animate-fade-in" data-testid="finance-dashboard">
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight mb-2" style={{ fontFamily: 'DM Sans' }}>Finance Dashboard</h1>
        <p className="text-muted-foreground text-sm mb-6">Invoices, fee schedules, pay rates, and organizations</p>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Pending Invoices', value: pendingInvoices.length, icon: Receipt, color: 'text-blue-600' },
            { label: 'Flagged Items', value: flaggedInvoices.length, icon: AlertTriangle, color: 'text-red-600' },
            { label: 'Organizations', value: orgs.length, icon: Building, color: 'text-[hsl(var(--brand))]' },
            { label: 'Fee Schedules', value: feeSchedules.length, icon: DollarSign, color: 'text-green-600' },
          ].map((s, i) => (
            <div key={i} className="rounded-lg border bg-card p-4">
              <div className="flex items-center justify-between"><p className="text-xs text-muted-foreground">{s.label}</p><s.icon className={`w-4 h-4 ${s.color}`} /></div>
              <p className="text-2xl font-bold mt-1" style={{ fontFamily: 'DM Sans' }}>{s.value}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue="invoices" className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="invoices" data-testid="finance-invoices-tab">Invoices ({invoices.length})</TabsTrigger>
            <TabsTrigger value="orgs" data-testid="finance-orgs-tab">Organizations</TabsTrigger>
            <TabsTrigger value="fee-schedules" data-testid="finance-fees-tab">Fee Schedules</TabsTrigger>
            <TabsTrigger value="pay-rates" data-testid="finance-pay-tab">Pay Rates</TabsTrigger>
          </TabsList>

          <TabsContent value="invoices">
            <div className="rounded-lg border bg-card">
              <ScrollArea className="h-[calc(100vh-24rem)]">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Invoice</TableHead>
                    <TableHead>Interpreter</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Match</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {invoices.map(inv => (
                      <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                        <TableCell className="font-mono text-xs">{inv.invoice_number}</TableCell>
                        <TableCell className="text-sm">{inv.interpreter_name}</TableCell>
                        <TableCell className="font-mono text-sm">${((inv.amount_cents || 0) / 100).toFixed(2)}</TableCell>
                        <TableCell>{statusBadge(inv.status)}</TableCell>
                        <TableCell>
                          {inv.has_discrepancy
                            ? <Badge className="bg-red-500/10 text-red-600 border-red-500/20 text-[10px] gap-1"><AlertTriangle className="w-3 h-3" />Flagged</Badge>
                            : <Badge className="bg-green-500/10 text-green-600 border-green-500/20 text-[10px] gap-1"><CheckCircle className="w-3 h-3" />OK</Badge>
                          }
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">{inv.created_at ? new Date(inv.created_at).toLocaleDateString() : '-'}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" className="h-7" onClick={() => viewInvoice(inv)} data-testid={`view-invoice-${inv.id}`}><Eye className="w-3.5 h-3.5 mr-1" />Review</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {invoices.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No invoices</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </ScrollArea>
            </div>
          </TabsContent>

          <TabsContent value="orgs">
            <div className="rounded-lg border bg-card p-1">
              <Table>
                <TableHeader><TableRow><TableHead>Organization</TableHead><TableHead>Plan</TableHead><TableHead>Credits</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
                <TableBody>
                  {orgs.map(o => (
                    <TableRow key={o.id}><TableCell className="font-medium">{o.name}</TableCell><TableCell><Badge variant="outline">{o.plan || 'free'}</Badge></TableCell><TableCell className="font-mono">{o.credit_balance || 0}</TableCell>
                      <TableCell><Button variant="ghost" size="sm" className="h-7" onClick={() => navigate('/admin/orgs')}>Manage</Button></TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="fee-schedules">
            <div className="rounded-lg border bg-card p-1">
              <Table>
                <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>AI Rate</TableHead><TableHead>Tax</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
                <TableBody>
                  {feeSchedules.map(f => (
                    <TableRow key={f.id}><TableCell className="font-medium">{f.name}</TableCell><TableCell className="font-mono">{f.ai_rate_cents_per_minute}c/min</TableCell><TableCell>{f.tax_enabled ? `${f.tax_rate_percent}%` : 'No'}</TableCell>
                      <TableCell><Button variant="ghost" size="sm" className="h-7" onClick={() => navigate('/admin/fee-schedules')}>Edit</Button></TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="pay-rates">
            <div className="rounded-lg border bg-card p-1">
              <Table>
                <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Rate</TableHead><TableHead>Min Call</TableHead><TableHead>Interpreters</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
                <TableBody>
                  {paySchedules.map(p => (
                    <TableRow key={p.id}><TableCell className="font-medium">{p.name}</TableCell><TableCell className="font-mono">{p.rate_cents_per_minute}c/min</TableCell><TableCell>{p.min_call_minutes}min</TableCell><TableCell>{p.interpreter_count || 0}</TableCell>
                      <TableCell><Button variant="ghost" size="sm" className="h-7" onClick={() => navigate('/admin/pay-rates')}>Edit</Button></TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>

        {/* Invoice Detail Dialog */}
        <Dialog open={!!selectedInvoice} onOpenChange={() => setSelectedInvoice(null)}>
          <DialogContent className="max-w-2xl" data-testid="invoice-detail-dialog">
            <DialogHeader>
              <DialogTitle>Invoice {selectedInvoice?.invoice_number}</DialogTitle>
              <DialogDescription>{selectedInvoice?.interpreter_name} — {statusBadge(selectedInvoice?.status)}</DialogDescription>
            </DialogHeader>
            {detailLoading ? <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div><p className="text-muted-foreground">Amount</p><p className="font-bold font-mono">${((selectedInvoice?.amount_cents || 0) / 100).toFixed(2)}</p></div>
                  <div><p className="text-muted-foreground">Items</p><p className="font-bold">{(selectedInvoice?.line_items || []).length}</p></div>
                  <div><p className="text-muted-foreground">Date</p><p className="font-bold">{selectedInvoice?.created_at ? new Date(selectedInvoice.created_at).toLocaleDateString() : '-'}</p></div>
                </div>
                {selectedInvoice?.description && <p className="text-sm border rounded p-3 bg-secondary">{selectedInvoice.description}</p>}

                {/* Line Items with validation */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold">Line Items</p>
                    {selectedInvoice?.status === 'submitted' && (
                      <Button size="sm" variant="outline" onClick={() => handleBatchApprove(selectedInvoice.id)} data-testid="batch-approve-btn">
                        <CheckCheck className="w-3.5 h-3.5 mr-1" />Approve All
                      </Button>
                    )}
                  </div>
                  <ScrollArea className="max-h-64">
                    {(selectedInvoice?.line_items || []).map((item, idx) => (
                      <div key={idx} className={`flex items-center gap-3 rounded-lg border p-3 mb-2 ${item.matches_system === false ? 'border-red-500/40 bg-red-500/5' : item.matches_system === true ? 'border-green-500/40 bg-green-500/5' : ''}`} data-testid={`line-item-${idx}`}>
                        <div className="flex-shrink-0">
                          {item.matches_system === true ? <CheckCircle className="w-5 h-5 text-green-600" /> : item.matches_system === false ? <XCircle className="w-5 h-5 text-red-600" /> : <div className="w-5 h-5 rounded-full border-2" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm truncate">{item.description || `Item ${idx + 1}`}</p>
                          <div className="flex gap-4 text-xs text-muted-foreground mt-0.5">
                            <span>Claimed: {item.duration_minutes ?? '-'}min</span>
                            {item.system_duration_minutes != null && <span>System: {item.system_duration_minutes}min</span>}
                            <span>{item.pay_rate_cents_per_minute ?? '-'}c/min</span>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="font-mono text-sm font-semibold">${((item.amount_cents || 0) / 100).toFixed(2)}</p>
                          {item.item_status === 'approved' ? <Badge className="bg-green-500/10 text-green-600 text-[10px]">Approved</Badge>
                            : selectedInvoice?.status === 'submitted' && (
                              <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => handleApproveItem(selectedInvoice.id, idx)} data-testid={`approve-item-${idx}`}>Approve</Button>
                            )}
                        </div>
                      </div>
                    ))}
                    {(selectedInvoice?.line_items || []).length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No line items</p>}
                  </ScrollArea>
                </div>
              </div>
            )}
            <DialogFooter className="gap-2">
              {selectedInvoice?.status === 'submitted' && (
                <>
                  <Button variant="destructive" size="sm" onClick={() => handleInvoiceAction(selectedInvoice.id, 'reject')} data-testid="reject-invoice-btn"><XCircle className="w-4 h-4 mr-1" />Reject</Button>
                  <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => handleInvoiceAction(selectedInvoice.id, 'approve')} data-testid="approve-invoice-btn"><CheckCircle className="w-4 h-4 mr-1" />Approve</Button>
                </>
              )}
              {selectedInvoice?.status === 'approved' && (
                <Button size="sm" className="bg-[hsl(var(--brand))] text-white" onClick={() => handleInvoiceAction(selectedInvoice.id, 'pay')} data-testid="pay-invoice-btn"><DollarSign className="w-4 h-4 mr-1" />Mark Paid</Button>
              )}
              <Button variant="outline" onClick={() => setSelectedInvoice(null)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppShell>
  );
}
